
import { STATUS_TYPES, VALIDATION_PATTERNS } from 'dyl-components';
import callActions from 'actions/call'
import officeView from 'actions/office_view';
import contactsActions from 'actions/contacts';

const googlePhoneUtil = require('google-libphonenumber').PhoneNumberUtil.getInstance();
const PNF = require('google-libphonenumber').PhoneNumberFormat;

export default class PhoneUtil {
    static formatDisplayedValue(isFocused, value) {
        if (!isFocused && value?.length > 0) {
            return PhoneUtil.formatPhoneNumber(value);
        }
        return value;
    }

    static handlePhonePaste(e, onChange, name) {
        const pasted = e.clipboardData.getData('Text');
        onChange({
            target: {
                name: name,
                value: PhoneUtil.getUnformatted(pasted, true),
            },
        });
        e.preventDefault();
    }

    static handlePhoneBlur(value, onChange, name, removeFocus) {
        removeFocus(name)
        onChange({
            target: {
                name: name,
                value: PhoneUtil.getUnformatted(value, true),
            },
        });
    }


    static formatPhoneNumber(phoneNumber, countryCode = 'US') {
        try {
            const phoneNumberString = `${phoneNumber}`;
            const formattedPhoneNumber = googlePhoneUtil.parseAndKeepRawInput(phoneNumberString, countryCode);
            return googlePhoneUtil.format(formattedPhoneNumber, PNF.NATIONAL);
        } catch (e) {
            return phoneNumber || 'N/A';
        }
    }

    static removeCountryCode(phoneNumber, countryCode = 'US') {
        return phoneNumber.replace(/^\+?1?/, '');
    }

    static formatPhoneNumbersIn(itemsContainingPhoneNumbers) {
        let formattedCallRecords = [];
        itemsContainingPhoneNumbers.forEach((item) => {
            let itemCopy = Object.assign({}, item);
            let phoneNumber = itemCopy.phone;
            itemCopy.phone = PhoneUtil.formatPhoneNumber(phoneNumber);
            formattedCallRecords.push(itemCopy);
        });
        return formattedCallRecords;
    }

    static asYouTypeFormatter(number, keepPrefix = true) {
        if (number) {
            let prefix = "";
            let numberAux = number;

            if (/^\*\d.*$/.test(number)) {
                prefix = `${number.substring(0, 2)} `;
                numberAux = number.substring(2) || "";
            }

            const stringLength = numberAux.length;
            if (numberAux) {
                if (stringLength > 9) { numberAux = this.formatPhoneNumber(numberAux) }
                else if (stringLength > 6) { numberAux = `${numberAux.slice(0, 3)}-${numberAux.slice(3, 6)}-${numberAux.slice(6)}`; }
                else if (stringLength > 4) { numberAux = `${numberAux.slice(0, 3)}-${numberAux.slice(3)}`; }
            }

            return `${keepPrefix ? prefix : ""}${numberAux}`.trim();
        }
        return "";
    }

    static getUnformatted(phoneNumber, onlyBasicCleanup = false) {
        const phoneStr = `${phoneNumber}`;

        if (onlyBasicCleanup) {
            const isPhoneLike = /^[\d\s().-]+$/.test(phoneStr);
            if (isPhoneLike) {
                return phoneStr.replace(/[\s().-]/g, '');
            }
            return phoneStr;
        }
        return phoneStr.replace(/\D/g, '');
    }

    static isValid(phoneNumber, countryCode = 'US') {
        let phoneTest = /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/;
        return phoneNumber && phoneTest.test(phoneNumber);
    }

    static formatPhoneNumberForPayload(phoneNumber) {
        return `+1${phoneNumber}`;
    }

    static isValidAreaCode(value) {
        return `${value}`.match(/^[0-9]{0,3}$/);
    }

    static sanitizePhoneNumber(value) {
        //Remove extra characters & remove appended 1
        const removeNonDigits = `${value}`.replace(/\D/g, "").replace(/^1/g, "");
        //Test for ONLY digits, if not pass original value.
        return VALIDATION_PATTERNS.number.test(removeNonDigits) ? removeNonDigits : value;
    }

    static onAddPhone(phone, phonesToEdit, addPhone) {
        const mainPhone = phonesToEdit.find((phoneToEdit) => phoneToEdit.main)
        phone.main = !mainPhone;
        addPhone(phone)
    }

    static insertExtension(extension, extensions) {
        const index = extensions.findIndex((ext) => ext.value > extension);

        if (index > -1) {
            const firstPart = extensions.slice(0, index);
            const secondPart = extensions.slice(index);
            return [...firstPart, { key: extension, value: extension, text: extension }, ...secondPart];
        }

        return [...extensions, { key: extension, value: extension, text: extension }];
    }
    static isUSPhoneNumber(value) {
        return new RegExp(/^\+?1?\d{10}$/).test(value)
    }

    static isPhoneNumber(value) {
        return new RegExp(/^\d+$/).test(value)
    }

    static C2D({ payload, dispatch }) {
        try {
            const { destination } = payload
            dispatch(callActions.call(payload));
            dispatch(officeView.currentCallPhoneNumber('CALL_CURRENT_NUMBER', destination));
        } catch (error) {
            console.log(error);
            Notification.alert('Failed to dial number.', STATUS_TYPES.ERROR)
        }
    }

    static isSoftPhone = (phone) => {
        return Object.keys(phone?.current).length !== 0;
    }

    static isHardPhone = (phone) => {
        return !this.isSoftPhone(phone);
    }

    static isPhoneSystemSupported = (data) => {
        return Object.keys(data || {})?.length !== 0;
    }

    static dialerCallIDs = (call, phone, user_id) => {
        if (!call) {
            return {};
        }
        let sessionID;
        const leg = this.contactLeg(call);
        const userLeg = call?.legs?.find(leg => leg.extension === user_id) || call.legs?.find(leg => leg.user_id);
        if (!userLeg) {
            return {}
        }
        sessionID = userLeg.channel_uuid;
        return { leg, userLeg, sessionID }
    }
    static phoneDeviceCallIDs = (call, phone, userPhoneCreds) => {
        if (!call) {
            return {};
        }
        let sessionID;
        const leg = this.contactLeg(call, userPhoneCreds);
        const userLeg = call?.legs?.find(leg => leg.extension === userPhoneCreds) || call.legs?.find(leg => leg.user_id);
        if (!userLeg) {
            return {}
        }
        if (call.call_type === 'transfer') {
            sessionID = call.legs.find(leg => phone?.current?.Sessions?.hasOwnProperty(leg?.channel_uuid))?.channel_uuid || call.sip_call_id;
        } else if (call.call_type === "click2dial") {
            sessionID = userLeg.channel_uuid === call.uuid ? call.uuid : userLeg.channel_uuid;
        } else if (call.call_type === "dialer" || call.isSystemCall) {
            sessionID = userLeg.channel_uuid;
        } else {
            sessionID = (call.direction === 'outbound' || (call.direction === 'internal' && userLeg.channel_uuid === call.uuid)) ? call.sip_call_id : userLeg.channel_uuid;
        }
        return { leg, userLeg, sessionID }
    }

    static phoneDeviceSession = (sessionID, phone) => {
        //if session_id exists, whether it is a softphone or hardphone
        return this.isSoftPhone(phone) && phone?.current?.getSession(sessionID);
    }

    static contactLeg = (call, userPhoneCreds) => {
        if (!call) {
            return;
        }
        if (call?.call_type === 'dialer') {
            return call?.legs?.find(l => !l.user_id)
        }
        if (call?.direction === 'internal') {
            let caller;
            if (call?.call_type === "click2dial") {
                caller = call?.legs.find(l => l.extension !== userPhoneCreds)
            } else {
                caller = call?.legs.find(l => l.channel_uuid === call.uuid)
            }
            if (caller && (caller.extension !== userPhoneCreds || call?.legs.length > 2)) {
                return caller
            }
        }
        if (call?.direction === 'inbound') {
            return call?.legs?.find(l => l.channel_uuid === call.uuid);
        } else if (call?.isSystemCall) {
            return call?.legs?.find(l => !l.user_id);
        } else {
            return call?.legs?.find(l => l.extension !== userPhoneCreds) || call?.legs?.find(leg => leg.user_id);
        }
    };

    static loadContactHeader = (calls, userPhoneCreds, uuid, dispatch) => {
        const answeredCall = calls[uuid];
        const answeredContactLeg = this.contactLeg(answeredCall, userPhoneCreds);
        const { contact_id, account_id } = answeredContactLeg || {};
        if (contact_id || account_id) {
            const id = contact_id || account_id;
            dispatch(contactsActions.viewLookup(id, { call_uuid: uuid }))
        } else {
            dispatch(officeView.contactLookup('CONTACT_LOOKUP', uuid));
        }
    }

    static handleDialerHoldOnAnswer = (dialer, calls, userPhoneCreds, dispatch) => {
        if (!dialer?.call_uuid) {
            return;
        }
        const dialerCall = calls?.[dialer.call_uuid];
        if (!dialerCall) {
            return;
        }
        if (dialerCall.ringing_agent) {
            const contactLeg = this.contactLeg(dialerCall, userPhoneCreds);
            dispatch(officeView.hangup({
                pbx_hostname: dialerCall.pbx || dialer.pbx,
                channel_uuid: contactLeg?.channel_uuid || dialer.agent_uuid
            }));
        } else if (dialerCall.status === 'active') {
            dispatch(officeView.actionButtons('CALL_ACTION_BUTTONS', dialer.call_uuid, { hold: true }));
        }
    }

    static getDisplayedIncomingCalls = (officeViewState) => {
        const userPhoneCreds = officeViewState.creds.extension;
        if (!userPhoneCreds) {
            return [];
        }
        const calls = officeViewState.calls;
        return officeViewState.extensions?.[userPhoneCreds]?.reduce((array, key) => {
            const call = calls[key]
            if (call && call.display && (
                call.call_type === "inbound" ||
                (call.call_type === "transfer" && call.legs.find(leg => leg?.extension === userPhoneCreds)) ||
                (call.direction === "internal" && (call.call_type === "click2dial" ?
                    (!!!call.ringing_agent && call.legs.find(leg => leg.extension === userPhoneCreds)?.channel_uuid !== call?.uuid) :
                    call.legs.find(leg => leg.extension === userPhoneCreds)?.channel_uuid !== call?.uuid
                )) ||
                (call.isSystemCall && call.call_type !== "click2dial" && !call.isPress1)
            )) {
                array.push(call)
            }
            return array;
        }, []) || []
    }

    static DESTINATION_TYPES_OPTIONS = [
        { key: "call_queue", value: "call_queue", text: "Call Queue", options: [] },
        { key: "conference_room", value: "conference_room", text: "Conference Room", options: [] },
        { key: "destination_group", value: "destination_group", text: "Destination Groups", options: [] },
        { key: "direct_extension", value: "direct_extension", text: "Direct Extension", options: [] },
        { key: "forwarding_number", value: "forwarding_number", text: "Forwarding Number", options: [] },
        { key: "ivr", value: "ivr", text: "IVR Prompt", options: [] },
        { key: "ring_group", value: "ring_group", text: "Ring Group", options: [] },
        { key: "routing_rule", value: "routing_rule", text: "Call Routing Rules", options: [] },
        { key: "parking_lot", value: "parking_lot", text: "Parking Lot", options: [] },
        { key: "personal_voicemail", value: "personal_voicemail", text: "Personal VM Box", options: [] },
        { key: "company_voicemail", value: "company_voicemail", text: "Company VM Box", options: [] },
    ]

    static EXIT_KEY_OPTIONS = [
        { key: "#", value: "#", text: "#" },
        { key: "*", value: "*", text: "*" },
        { key: "0", value: "0", text: "0" },
        { key: "1", value: "1", text: "1" },
        { key: "2", value: "2", text: "2" },
        { key: "3", value: "3", text: "3" },
        { key: "4", value: "4", text: "4" },
        { key: "5", value: "5", text: "5" },
        { key: "6", value: "6", text: "6" },
        { key: "7", value: "7", text: "7" },
        { key: "8", value: "8", text: "8" },
        { key: "9", value: "9", text: "9" },
    ];

    static DESTINATION_TYPES = [
        { key: "none", value: "none", text: "None" },
        { key: "call_queue", value: "call_queue", text: "Call Queue" },
        { key: "destination_group", value: "destination_group", text: "Destination Groups" },
        { key: "direct_extension", value: "direct_extension", text: "Direct Extension" },
        { key: "forwarding_number", value: "forwarding_number", text: "Forwarding Number" },
        { key: "ivr", value: "ivr", text: "IVR Prompt" },
        { key: "ring_group", value: "ring_group", text: "Ring Group" },
        { key: "routing_rule", value: "routing_rule", text: "Call Routing Rules" },
        { key: "personal_voicemail", value: "personal_voicemail", text: "Personal VM Box" },
        { key: "company_voicemail", value: "company_voicemail", text: "Company VM Box" },
    ]

    static SOUND_TYPES = [
        { key: "general", value: "general", text: "General" },
        { key: "personal_voicemail", value: "personal_voicemail", text: "Personal VM Box" },
        { key: "company_voicemail", value: "company_voicemail", text: "Company VM Box" },
        { key: "ivr", value: "ivr", text: "IVR Prompt" },
        { key: "vmdrop", value: "vmdrop", text: "Voicemail Drop" },
        { key: "music", value: "music", text: "Music on Hold" },
    ]

    static END_OF_LINE_TYPES = ["personal_voicemail", "company_voicemail", "ivr"];

    static DESTINATION_GROUPS = ["conference_room", "destination_group", "routing_rule", "parking_lot", "personal_voicemail"];

    static RING_DURATION_OPTIONS = [
        { key: 15, value: 15, text: "15 seconds" },
        { key: 30, value: 30, text: "30 seconds" },
        { key: 60, value: 60, text: "60 seconds" },
        { key: 90, value: 90, text: "90 seconds" },
        { key: 120, value: 120, text: "2 minutes" },
        { key: 300, value: 300, text: "5 minutes" },
        { key: 900, value: 900, text: "15 minutes" },
        { key: 1800, value: 1800, text: "30 minutes" },
        { key: 3600, value: 3600, text: "1 hour" },
        { key: 7200, value: 7200, text: "2 hours" },
    ]

    static DESTINATION_TYPES_ICONS = {
        direct_extension: "fa-solid fa-circle-user",
        forwarding_number: "fa-solid fa-arrow-right-from-line",
        call_queue: "fa-solid fa-user-clock",
        ring_group: "fa-solid fa-phone-volume",
        ivr: "fa-solid fa-file-audio",
        routing_rule: "fa-solid fa-route",
        personal_voicemail: "fa-solid fa-voicemail",
        company_voicemail: "fa-solid fa-voicemail",
        conference_room: "fa-solid fa-people",
        parking_lot: "fa-solid fa-car",
        destination_group: "fa-solid fa-rectangle-list",
    };

    static DESTINATION_TYPES_AUDIO_ICONS = {
        ...this.DESTINATION_TYPES_ICONS,
        conference_room: "fa-solid fa-people",
        parking_lot: "fa-solid fa-car",
        phone_number: "fa-solid fa-phone",
    }

    static DESTINATION_TYPES_URLS = {
        forwarding_number: "/settings/phone-management/numbers/external-numbers",
        ring_group: "/settings/phone-management/routing/ring-group-call-queues",
        call_queue: "/settings/phone-management/routing/ring-group-call-queues",
        ivr: "/settings/phone-management/routing/ivr-prompts",
        routing_rule: "/settings/phone-management/routing/call-routing-rules",
        company_voicemail: "/settings/phone-management/extensions/company",
        personal_voicemail: "/settings/phone-management/extensions/users",
        destination_group: "/settings/phone-management/routing/destination-groups",
        phone_number: "/settings/phone-management/numbers/dyl-numbers"
    }

    static COMPANY_EXTENSION_TYPES = [
        { key: "company_voicemail", value: "company_voicemail", text: "Company VM Box" },
        { key: "conference_room", value: "conference_room", text: "Conference Room" },
        { key: "parking_lot", value: "parking_lot", text: "Parking Lot" },
    ]

    static DEVICE_TYPES_OPTIONS = [
        { key: "browser", value: "browser", text: "DYL Browser Softphone", type: "Softphone" },
        { key: "hardphone", value: "hardphone", text: "Hardphone", type: "Hardphone" },
        { key: "softphone", value: "softphone", text: "External Softphone", type: "Softphone" },
    ]

    static CALL_RESULT_OPTIONS = [
        { key: "Reached", value: "Reached", text: "Reached" },
        { key: "Not Reached", value: "Not Reached", text: "Not Reached" },
        { key: "Voicemail Left", value: "Voicemail Left", text: "Voicemail Left" },
        { key: "Voicemail Not Left", value: "Voicemail Not Left", text: "Voicemail Not Left" },
        { key: "Voicemail Box Full", value: "Voicemail Box Full", text: "Voicemail Box Full" },
    ]

    static CALL_TYPE_OPTIONS = [
        { key: "Dialphone", value: "dialphone", text: "Dialphone" },
        { key: "Inbound", value: "inbound", text: "Inbound" },
        { key: "Click2dial", value: "click2dial", text: "Click2dial" },
        { key: "Transfer", value: "transfer", text: "Transfer" },
        { key: "Dialer", value: "dialer", text: "Dialer" },
        { key: "Newlead", value: "newlead", text: "Newlead" },
        { key: "Sequence", value: "sequence", text: "Sequence" },
        { key: "Callback", value: "callback", text: "Callback" },
        { key: "Task", value: "task", text: "Task" }
    ]

    static timeTicker(time) {
        if (time <= 0 || isNaN(time)) {
            return "00:00:00"
        }
        const hours = Math.floor(time / 3600);
        const minutes = Math.floor((time % 3600) / 60);
        const remainingSeconds = time % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }

    static SYSTEM_RESULT_OPTIONS = [
        { value: "abandoned", text: "Abandoned" },
        { value: "busy", text: "Busy" },
        { value: "connected", text: "Connected" },
        { value: "conference", text: "Conference" },
        { value: "disconnect", text: "Disconnected Number" },
        { value: "missed", text: "Missed" },
        { value: "missedattempt", text: "Missed Attempt" },
        { value: "notanswered", text: "No-Answer" },
        { value: "notconnected", text: "Not Connected" },
        { value: "transfer", text: "Transferred" },
        { value: "voicemail_drop", text: "Voicemail Drop" },
    ];

    static DIRECTION_OPTIONS = [
        { value: "inbound", text: "Inbound" },
        { value: "outbound", text: "Outbound" },
        { value: "internal", text: "Internal" },
    ];

    static VOICEMAIL_OPTIONS = [
        { value: "yes", text: "Listened" },
        { value: "no", text: "Not Listened" },
    ];

    static CALL_HISTORY_FILTER_PARAMS = [
        "user_id",
        "destination",
        "phone",
        "start_at",
        "end_at",
        "system_result",
        "user_result",
        "direction",
        "voicemail_listened",
        "tags",
        "call_type"
    ];

    static CALL_ISSUE_OPTIONS = {
        keys: [
            "Dropped Call",
            "Call Quality",
            "One Way Audio",
            "Missing Recording",
            "Dropped Transfered Call",
        ],
        getOptions: function () {
            return this.keys.map((issue) => ({
                key: issue,
                value: issue,
                text: issue,
            }));
        },
    };

    static isDialerRunning = (dialer) => {
        return dialer?.campaign_id;
    }
}
